// src/pages/AboutUs/AboutUsPage.jsx
import React, { lazy, Suspense, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";

// Keep light components sync, heavy ones lazy
import AboutUsBanner from "../../component/AboutUs/AboutUsBanner/AboutUsBanner";
const CompanyOverview   = lazy(() => import("../../component/AboutUs/CompanyOverview/CompanyOverview"));
const Timeline          = lazy(() => import("../../component/AboutUs/MissionVisionGoals/Timeline"));
const OfferSection      = lazy(() => import("../../component/AboutUs/WhatWeOffer/OfferSection"));
const FeaturesSection   = lazy(() => import("../../component/AboutUs/WhyNextech/FeaturesSection"));
const OurTeam           = lazy(() => import("../../component/AboutUs/OurTeams/OurTeam"));
// const LeaderShipTeam = lazy(() => import("../../component/AboutUs/LeaderShipTeam/LeaderShipTeam"));
const ContactForm       = lazy(() => import("../../component/ContactUs/ContactUsForm/ContactForm"));
const FloatingContactUs = lazy(() => import("../../component/floting_contact/FloatingContactUs"));

/* =========================
   ✨ Glass + Parallax Styles
   ========================= */
const PageWrap = styled.main`
  /* soft background to make glass pop */
  background:
    radial-gradient(160% 120% at 0% 0%, #ffffff 0%, #f6f9fc 58%, #eef4f9 100%);
`;

/* Banner shell with parallax light glows */
const ParallaxBanner = styled.section`
  position: relative;
  isolation: isolate; /* so glows don't bleed outside */
  overflow: hidden;

  /* top ambient glow */
  &::before {
    content: "";
    position: absolute; inset: -20%;
    background:
      radial-gradient(50% 38% at 10% 0%, rgba(16,181,255,.18), transparent 60%),
      radial-gradient(40% 34% at 92% 0%, rgba(122,227,255,.18), transparent 70%);
    filter: blur(30px);
    z-index: 0;
    pointer-events: none;
  }
`;

/* light parallax blobs behind the banner */
const BannerParallaxLayer = styled.div`
  position: absolute; inset: -10% -10% auto -10%;
  height: 60%;
  z-index: 0;
  background:
    radial-gradient(40% 30% at 20% 70%, rgba(0,146,255,.10), transparent 70%),
    radial-gradient(36% 28% at 80% 30%, rgba(0,198,255,.10), transparent 70%);
  transform: translateY(var(--py, 0px));
  will-change: transform;
`;

/* generic glass wrapper to place around your sections */
const GlassPanel = styled.section`
  position: relative;
  margin: 24px 0;
  .glass {
    position: relative;
    border-radius: 20px;
    border: 1px solid rgba(6, 51, 75, .10);
    background:
      linear-gradient(180deg, rgba(255,255,255,.70), rgba(255,255,255,.55));
    /* frost */
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    box-shadow:
      0 18px 48px rgba(12, 36, 54, .10),
      inset 0 1px 0 rgba(255,255,255,.75);
    padding: clamp(18px, 3vw, 28px);
    will-change: transform;
    transform-style: preserve-3d;
    transition: transform 180ms cubic-bezier(.2,.7,.2,1), box-shadow 180ms;
  }

  /* subtle 3D hover effect (when JS tilt is off / reduced motion) */
  @media (hover: hover) {
    .glass:hover {
      box-shadow:
        0 26px 60px rgba(12, 36, 54, .16),
        inset 0 1px 0 rgba(255,255,255,.80);
      transform: translateY(-4px);
    }
  }
`;

/* thin accent line between sections (parallax slight) */
const SectionAccent = styled.div`
  height: 10px;
  margin: 12px 0 4px;
  border-radius: 999px;
  background: linear-gradient(90deg, #07b6f2, #63e2ff, #b7f1ff);
  filter: blur(0.2px);
  opacity: .4;
  transform: translateY(var(--py, 0px));
  will-change: transform;
`;

/* ================ */
/*  Utility Hooks   */
/* ================ */
const useScrollToTop = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
};

/* Parallax for any element with data-parallax (sets --py) */
const useParallax = () => {
  useEffect(() => {
    const items = Array.from(document.querySelectorAll("[data-parallax]"));
    if (!items.length) return;
    let raf = null;

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset || 0;
        items.forEach((el) => {
          const speed = parseFloat(el.getAttribute("data-parallax")) || 0.2;
          el.style.setProperty("--py", `${Math.round(y * speed)}px`);
        });
        raf = null;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
};

/* Simple mouse-tilt for anything with data-tilt (applies 3D rotate)
   respects prefers-reduced-motion */
const useTilt = () => {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const nodes = Array.from(document.querySelectorAll("[data-tilt]"));
    if (!nodes.length) return;

    const onMove = (e) => {
      const el = e.currentTarget;
      const target = el.querySelector(".glass") || el;
      const r = target.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const rx = -((y - r.height / 2) / (r.height / 2)) * 6;
      const ry = ((x - r.width / 2) / (r.width / 2)) * 6;
      target.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(0)`;
    };

    const onLeave = (e) => {
      const target = (e.currentTarget.querySelector(".glass") || e.currentTarget);
      target.style.transform = "";
    };

    nodes.forEach((n) => {
      n.addEventListener("mousemove", onMove);
      n.addEventListener("mouseleave", onLeave);
    });

    return () => {
      nodes.forEach((n) => {
        n.removeEventListener("mousemove", onMove);
        n.removeEventListener("mouseleave", onLeave);
      });
    };
  }, []);
};

/* ========================= */
/*         Component         */
/* ========================= */
const AboutUsPage = () => {
  useScrollToTop();
  useParallax();
  useTilt();

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "NexTech PLM Solutions",
    "url": "https://www.nextechplm.com/about",
    "logo": "https://www.nextechplm.com/static/logo.png",
    "sameAs": ["https://www.linkedin.com/company/nextech-plm-solutions"],
    "contactPoint": [{
      "@type": "ContactPoint",
      "telephone": "+91-XXXXXXXXXX",
      "contactType": "sales",
      "areaServed": "IN",
      "availableLanguage": ["en"]
    }]
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",     "item": "https://www.nextechplm.com/" },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.nextechplm.com/about" }
    ]
  };

  return (
    <PageWrap>
      <Helmet>
        <title>About Us | NexTech PLM Solutions — Teamcenter & Engineering Services</title>
        <meta
          name="description"
          content="NexTech PLM Solutions helps manufacturers accelerate digital transformation with Teamcenter implementations, CAD/CAM/CAE services, integrations, and managed support. Meet our team, vision, and delivery model."
        />
        <meta name="keywords" content="about NexTech PLM, PLM company Pune, Teamcenter experts India, NexTech PLM team, PLM consulting company, engineering services Pune, digital transformation PLM" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://nextechplm.com/about" />

        {/* Open Graph */}
        <meta property="og:site_name" content="NexTech PLM Solutions" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About NexTech PLM Solutions — Teamcenter & Engineering Services" />
        <meta property="og:description" content="NexTech PLM Solutions helps manufacturers accelerate digital transformation with Teamcenter implementations, CAD/CAM/CAE services, integrations, and managed support." />
        <meta property="og:url" content="https://nextechplm.com/about" />
        <meta property="og:image" content="https://nextechplm.com/logo192.png" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About NexTech PLM Solutions — Teamcenter & Engineering Services" />
        <meta name="twitter:description" content="NexTech PLM Solutions helps manufacturers accelerate digital transformation with Teamcenter implementations, CAD/CAM/CAE services, and managed support." />
        <meta name="twitter:image" content="https://nextechplm.com/logo192.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      {/* ===== Banner with parallax accents ===== */}
      <ParallaxBanner>
        <BannerParallaxLayer data-parallax="0.15" aria-hidden="true" />
        <AboutUsBanner /> {/* no change inside your component */}
      </ParallaxBanner>

      <Suspense fallback={<div className="container py-5">Loading…</div>}>

        {/* ===== Company Overview (Glass + 3D tilt) ===== */}
        <GlassPanel id="overview" aria-labelledby="overview-heading" data-tilt>
          <div className="container glass">
            <CompanyOverview />
          </div>
        </GlassPanel>

        {/* decorative accent with parallax */}
        <SectionAccent data-parallax="0.07" aria-hidden="true" />

        {/* ===== What We Offer ===== */}
        <GlassPanel id="offer" aria-labelledby="offer-heading" data-tilt>
          <div className="container glass">
            <OfferSection />
          </div>
        </GlassPanel>

        <SectionAccent data-parallax="0.09" aria-hidden="true" />

        {/* ===== Mission / Vision / Goals Timeline ===== */}
        <GlassPanel id="mission" aria-labelledby="mission-heading" data-tilt>
          <div className="container glass">
            <Timeline />
          </div>
        </GlassPanel>

        <SectionAccent data-parallax="0.06" aria-hidden="true" />

        {/* ===== Why NexTech ===== */}
        <GlassPanel id="why-nextech" aria-labelledby="why-heading" data-tilt>
          <div className="container glass">
            <FeaturesSection />
          </div>
        </GlassPanel>

        <SectionAccent data-parallax="0.08" aria-hidden="true" />

        {/* ===== Team ===== */}
        <GlassPanel id="team" aria-labelledby="team-heading" data-tilt>
          <div className="container glass">
            <OurTeam />
          </div>
        </GlassPanel>

        {/* Optionally later
        <GlassPanel id="leadership" aria-labelledby="leadership-heading" data-tilt>
          <div className="container glass">
            <LeaderShipTeam />
          </div>
        </GlassPanel>
        */}

        <SectionAccent data-parallax="0.05" aria-hidden="true" />

        {/* ===== Contact ===== */}
        <GlassPanel id="contact" aria-labelledby="contact-heading" data-tilt>
          <div className="container glass">
            <ContactForm />
          </div>
        </GlassPanel>

        {/* defer floating widget (lightweight and outside glass) */}
        <FloatingContactUs />
      </Suspense>
    </PageWrap>
  );
};

export default AboutUsPage;
